import type {
  TaskPriority,
  TodoListMember,
  UserTodoItem,
} from '@dreamai/types';

// Mirrors `api/todo-list-guest.ts` in @dreamai/types (usedreamai/dream-types#50);
// switch to the package import once a release including it is published.
export type TodoListGuestChannel = 'email' | 'phone';

/**
 * What the "enter your code" screen needs, returned with a 401 on the list
 * routes when this browser has not been verified for the link yet.
 */
export interface TodoListGuestVerificationInfo {
  channel: TodoListGuestChannel;
  /** Masked address, e.g. `c•••@example.com` or `+44 ••• ••• 789`. */
  destination: string;
  listName: string;
  inviterName: string;
}

export interface TodoListGuestViewResponse {
  success: boolean;
  list?: { id: string; name: string };
  inviter?: { name: string; assistantName: string | null };
  guest?: { userId: string; name: string; channel: TodoListGuestChannel };
  members?: TodoListMember[];
  todos?: UserTodoItem[];
  error?: string;
  /** 401 only: the link is live but this browser has not verified yet. */
  verificationRequired?: boolean;
  verification?: TodoListGuestVerificationInfo;
  /** Only on failure (expired / revoked link): WhatsApp sign-up link. */
  whatsappUrl?: string;
}

export interface TodoListGuestSendCodeResponse {
  success: boolean;
  status?: 'sent' | 'cooldown';
  retryAfterSeconds?: number;
  expiresInSeconds?: number;
  verification?: TodoListGuestVerificationInfo;
  error?: string;
}

export type TodoListGuestVerifyStatus =
  | 'verified'
  | 'invalid'
  | 'expired'
  | 'too_many_attempts';

export interface TodoListGuestVerifyCodeResponse {
  success: boolean;
  status?: TodoListGuestVerifyStatus;
  deviceToken?: string;
  attemptsLeft?: number;
  error?: string;
}

export interface UpdateTodoListGuestTodoResponse {
  success: boolean;
  todo?: UserTodoItem;
  error?: string;
}

export interface CreateTodoListGuestTodoRequest {
  description: string;
  /** ISO date (YYYY-MM-DD) */
  dueDate?: string;
  priority?: TaskPriority;
  /** An accepted member of the list (the guest themself included). */
  assigneeUserId?: string;
}

export interface CreateTodoListGuestTodoResponse {
  success: boolean;
  todo?: UserTodoItem;
  error?: string;
}

export interface TodoListGuestCtaResponse {
  success: boolean;
  whatsappUrl?: string;
  error?: string;
}

export interface TodoListGuestWhatsAppSignupResponse {
  success: boolean;
  outcome?: 'sent' | 'already_on_dream' | 'already_invited';
  error?: string;
}

/**
 * Client for the public, token-gated `/todo-list-guest` API. The guest token
 * from the invite link travels in the `X-Guest-Token` header, never in a URL.
 * The list routes also want `X-Guest-Device`: a token this browser earned by
 * typing the one-time code sent to the invited address. It lives in
 * localStorage keyed by link token, so a re-invite (new link) starts clean.
 */

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const TOKEN_HEADER = 'X-Guest-Token';
const DEVICE_HEADER = 'X-Guest-Device';
const DEVICE_STORAGE_PREFIX = 'dream.todoGuestDevice.';

const deviceStorageKey = (token: string) => `${DEVICE_STORAGE_PREFIX}${token}`;

export function getGuestDevice(token: string): string | null {
  try {
    return window.localStorage.getItem(deviceStorageKey(token));
  } catch {
    return null;
  }
}

function setGuestDevice(token: string, deviceToken: string | null) {
  try {
    if (deviceToken) {
      window.localStorage.setItem(deviceStorageKey(token), deviceToken);
    } else {
      window.localStorage.removeItem(deviceStorageKey(token));
    }
  } catch {
    // Private mode / storage disabled: the guest just verifies again next time.
  }
}

export class GuestApiError extends Error {
  status: number;
  whatsappUrl?: string;
  /** Set on a 401: the link is live, this browser needs to enter a code. */
  verification?: TodoListGuestVerificationInfo;

  constructor(
    status: number,
    message: string,
    extra?: {
      whatsappUrl?: string;
      verification?: TodoListGuestVerificationInfo;
    },
  ) {
    super(message);
    this.status = status;
    this.whatsappUrl = extra?.whatsappUrl;
    this.verification = extra?.verification;
  }

  get verificationRequired() {
    return this.status === 401 && !!this.verification;
  }
}

interface GuestPayload {
  success: boolean;
  error?: string;
  whatsappUrl?: string;
  verificationRequired?: boolean;
  verification?: TodoListGuestVerificationInfo;
}

async function request<T extends GuestPayload>(
  token: string,
  path: string,
  init?: {
    method?: 'GET' | 'PATCH' | 'POST';
    body?: unknown;
    /** Verification endpoints only need the link token. */
    withDevice?: boolean;
  },
): Promise<T> {
  const device = init?.withDevice === false ? null : getGuestDevice(token);
  const response = await fetch(`${API_BASE}/todo-list-guest${path}`, {
    method: init?.method ?? 'GET',
    headers: {
      [TOKEN_HEADER]: token,
      ...(device ? { [DEVICE_HEADER]: device } : {}),
      ...(init?.body !== undefined
        ? { 'Content-Type': 'application/json' }
        : {}),
    },
    body: init?.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
  const payload = (await response.json().catch(() => null)) as T | null;
  if (!response.ok || !payload?.success) {
    if (response.status === 401 && device) {
      // Revoked (re-invite) or never valid here: forget it.
      setGuestDevice(token, null);
    }
    throw new GuestApiError(
      response.status,
      payload?.error || `Request failed (${response.status})`,
      {
        whatsappUrl: payload?.whatsappUrl,
        verification: payload?.verificationRequired
          ? payload.verification
          : undefined,
      },
    );
  }
  return payload;
}

export const fetchGuestList = (token: string) =>
  request<TodoListGuestViewResponse>(token, '/');

export const setGuestTodoStatus = (
  token: string,
  todoId: string,
  status: 'pending' | 'complete',
) =>
  request<UpdateTodoListGuestTodoResponse>(
    token,
    `/todos/${encodeURIComponent(todoId)}`,
    { method: 'PATCH', body: { status } },
  );

export const createGuestTodo = (
  token: string,
  body: CreateTodoListGuestTodoRequest,
) =>
  request<CreateTodoListGuestTodoResponse>(token, '/todos', {
    method: 'POST',
    body,
  });

export const sendGuestCode = (token: string) =>
  request<TodoListGuestSendCodeResponse>(token, '/verify/send', {
    method: 'POST',
    withDevice: false,
  });

/**
 * Swap a typed code for a device token. A non-`verified` status resolves (not
 * throws) so the screen can show what to do next; on success the device token
 * is stored for the link and sent on every following request.
 */
export async function verifyGuestCode(
  token: string,
  code: string,
): Promise<TodoListGuestVerifyCodeResponse> {
  const response = await fetch(`${API_BASE}/todo-list-guest/verify`, {
    method: 'POST',
    headers: { [TOKEN_HEADER]: token, 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  // A dead link answers with the generic 404 body (incl. the sign-up URL).
  const payload = (await response.json().catch(() => null)) as
    | (TodoListGuestVerifyCodeResponse & { whatsappUrl?: string })
    | null;
  if (response.ok && payload?.success && payload.deviceToken) {
    setGuestDevice(token, payload.deviceToken);
    return payload;
  }
  if (response.status === 401 && payload?.status) {
    return payload;
  }
  throw new GuestApiError(
    response.status,
    payload?.error || `Request failed (${response.status})`,
    { whatsappUrl: payload?.whatsappUrl },
  );
}

export const recordGuestCta = (token: string) =>
  request<TodoListGuestCtaResponse>(token, '/cta', { method: 'POST' });

export const startGuestWhatsAppSignup = (token: string, phoneNumber: string) =>
  request<TodoListGuestWhatsAppSignupResponse>(token, '/whatsapp-signup', {
    method: 'POST',
    body: { phoneNumber },
  });
